import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';

import { ConfigConstant } from '@shared/constants/config.constant';
import { RoutesUtil } from '@shared-features/utils/routes.util';
import { AvlReplay } from '@features/live-tracking/models/avl-replay.model';
import { GoogleMap } from '@angular/google-maps';
import { LocationsApiService } from '@shared-features/services/locations-api.service';
import { IconType } from '@shared/enums/icon-type.enum';
import { BaseComponent } from '../base-component/base.component';
import { Tracking } from '@features/live-tracking/models/tracking-driver.model';
import { Coordinate } from '@features/live-tracking/models/coordinate.model';
import { RoutesConstants } from '@shared/constants';
import { ShipmentAction, ShipmentsListType } from '@shared-features/enums';

@Component({
  selector: 'app-shipment-tracking-map',
  templateUrl: './shipment-tracking-map.component.html',
  styleUrls: ['./shipment-tracking-map.component.scss'],
})
export class ShipmentTrackingMapComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() points: Tracking[] | Coordinate[] = [];
  @Input() truckPoints: Coordinate[] = [];
  @Input() currentPoint: Tracking;
  @Input() history: any[];
  @Input() shipmentId: number;
  @Input() shipmentName: string;
  @Input() pickupAddress: string;
  @Input() dropOffAddress: string;
  @Input() pickupCoordinate: Coordinate;
  @Input() dropOffCoordinate: Coordinate;
  @Input() contentFillTheScreen = false;
  @Input() avlReplay: AvlReplay;
  @Input() showCoordinateInfo = true;
  @ViewChild('trackingMap') trackingMap: GoogleMap;
  viewedRoutes = {
    BEFORE_START: true,
    AFTER_START: true,
    STANDALONE: true,
  };

  beforeTripPoints: Tracking[] | Coordinate[] = [];
  tripPoints: Tracking[] | Coordinate[] = [];
  latitude: number = ConfigConstant.defaultLatitude;
  longitude: number = ConfigConstant.defaultLongitude;
  zoom = 4;
  sharedConstants = RoutesConstants;
  routesUtil = RoutesUtil;
  activeStopPositionId: number;
  activeOverSpeedPositionId: string;
  showFadeIn = false;
  mapLoaded = false;
  markerSize: 'xs' | 'sm' | 'lg' | 'md' = 'md';
  protected IconType = IconType;

  get center(): google.maps.LatLngLiteral {
    return {
      lat: this.latitude,
      lng: this.longitude,
    };
  }

  protected readonly ShipmentsListType = ShipmentsListType;

  constructor(
    private changeDetector: ChangeDetectorRef,

    private locationApiService: LocationsApiService
  ) {
    super();
  }

  ngOnInit(): void {
    this.locationApiService.loadGoogleMapsApi().then(() => {
      this.mapLoaded = true;
      this.changeDetector.markForCheck();
    });
  }

  ngOnChanges({ points, currentPoint, truckPoints }: SimpleChanges): void {
    if (points && points.currentValue && points.currentValue.length) {
      const index = this.points?.length > 1 ? Math.ceil(this.points.length / 2) : 0;
      this.latitude = this.points[index].latitude;
      this.longitude = this.points[index].longitude;
      this.zoom = this.getZoom(
        this.points[0].latitude,
        this.points[0].longitude,
        this.points[this.points.length - 1].latitude,
        this.points[this.points.length - 1].longitude
      );

      this.getTripPoints();
    }
  }

  showStopPopup(positionId: number, centerMap = false): void {
    this.activeStopPositionId = positionId;
    if (centerMap) {
      const stop = this.avlReplay.stops?.find((s) => s.positionId === positionId);
      if (stop) {
        this.centerMap(stop.latitude, stop.longitude);
      }
    }
  }

  showOverSpeedPopup(positionId: string): void {
    this.activeOverSpeedPositionId = positionId;
  }

  centerMap(latitude, longitude): void {
    if (latitude && longitude) {
      this.latitude = latitude;
      this.longitude = longitude;
    }
  }

  toggleRoute(routeName: string): void {
    this.viewedRoutes[routeName] = !this.viewedRoutes[routeName];
  }

  private latRad(lat) {
    const sin = Math.sin((lat * Math.PI) / 180);
    const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
    return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
  }

  private getZoom(lat_a, lng_a, lat_b, lng_b) {
    const latDif = Math.abs(this.latRad(lat_a) - this.latRad(lat_b));
    const lngDif = Math.abs(lng_a - lng_b);

    const latFrac = latDif / Math.PI;
    const lngFrac = lngDif / 360;

    const lngZoom = Math.log(1.001 / latFrac) / Math.log(2);
    const latZoom = Math.log(1.001 / lngFrac) / Math.log(2);
    let returnedZoom = Math.min(lngZoom, latZoom);
    if (returnedZoom > 15 || returnedZoom == Infinity) {
      returnedZoom = 15;
    }
    return returnedZoom;
  }

  private getTripPoints(): void {
    const history = this.history || [];
    const pickupPoint = history.find((item) => item.action === ShipmentAction.ArrivePickupLocation);
    const loadedPoint = history.find((item) => item.action === ShipmentAction.GoodsLoaded);
    this.tripPoints = [];
    this.beforeTripPoints = [];

    (this.points || []).forEach((point) => {
      const pickup = pickupPoint && new Date(pickupPoint.createdAt).getTime();
      const { deviceTime, serverTime } = point as any;
      const pointTime = new Date(deviceTime || serverTime).getTime();
      if (pickup && pointTime >= pickup) {
        this.tripPoints.push(point as any);
      } else if (!pickup || pointTime < pickup) {
        this.beforeTripPoints.push(point as any);
      }
    });
    if (this.beforeTripPoints?.length && this.tripPoints?.length) {
      this.tripPoints.unshift(this.beforeTripPoints[this.beforeTripPoints.length - 1] as any);
    }
  }

  handleZoomChanges(): void {
    const zoom = this.trackingMap.getZoom();
    this.markerSize = zoom < 11 ? (zoom >= 7 ? 'sm' : 'xs') : 'md';
  }
}
